import showdown from 'showdown';

export const deltaToMarkdown = ({ deltaData }) => {
  const lines = [];
  let currentLine = '';
  const listCounters = [];

  function formatInline(text, attrs = {}) {
    if (!text) return '';
    if (attrs.code) text = `\`${text}\``;
    if (attrs.strike) text = `~~${text}~~`;
    if (attrs.bold) text = `**${text}**`;
    if (attrs.italic) text = `*${text}*`;
    if (attrs.underline) text = `<u>${text}</u>`;
    // Add more inline formatting if needed
    return text;
  }

  for (let i = 0; i < deltaData.length; i++) {
    const op = deltaData[i];

    if (typeof op.insert === 'string') {
      const parts = op.insert.split('\n');

      for (let idx = 0; idx < parts.length; idx++) {
        const part = parts[idx];
        const isLastPart = idx === parts.length - 1;
        const attrs = op.attributes || {};
        const indent = attrs.indent || 0;

        if (part) {
          currentLine += formatInline(part, attrs);
        }

        if (!isLastPart) {
          let prefix = '';

          if (attrs.header) {
            prefix = '#'.repeat(attrs.header) + ' ';
          } else if (attrs.list) {
            if (attrs.list === 'ordered') {
              // Manage list counters stack for nested ordered list
              while (listCounters.length <= indent) {
                listCounters.push(0);
              }
              while (listCounters.length > indent + 1) {
                listCounters.pop();
              }

              listCounters[indent]++;
              // Reset counters for deeper levels
              for (let j = indent + 1; j < listCounters.length; j++) {
                listCounters[j] = 0;
              }

              prefix = '\t'.repeat(indent) + `${listCounters[indent]}. `;
            } else if (attrs.list === 'bullet') {
              prefix = '\t'.repeat(indent) + '* ';
            }
          } else {
            // Not a list or header - reset counters since paragraph break
            listCounters.length = 0;
          }

          lines.push(prefix + currentLine.trim());
          currentLine = '';
        }
      }
    } else if (op.insert?.formula) {
      currentLine += `$${op.insert.formula}$`;
    } else {
      currentLine += String(op.insert);
    }
  }

  if (currentLine.trim()) {
    lines.push(currentLine);
  }

  return lines.join('\n');
};

const converter = new showdown.Converter({
  simplifiedAutoLink: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  ghCodeBlocks: true
});

export const markdownToDelta = ({ markdownData, editor }) => {
  const parts = parseMarkdownWithFormulas({ markdown: markdownData });
  let index = editor.getSelection()?.index || editor.getLength();

  // Step 1: Split into logical lines
  const lines = [];
  let currentLine = [];

  parts.forEach((part) => {
    const segments = part.value.split(/\r?\n/);

    segments.forEach((seg, i) => {
      if (i > 0) {
        lines.push(currentLine);
        currentLine = [];
      }

      if (part.type === 'formula' || part.type === 'blockFormula') {
        currentLine.push({ type: part.type, value: part.value });
      } else if (seg.trim() !== '') {
        currentLine.push({ type: 'text', value: seg });
      }
    });
  });

  if (currentLine.length > 0) lines.push(currentLine);

  // Step 2: Remove empty lines
  const filteredLines = lines.filter((line) =>
    line.some(
      (part) =>
        part.type === 'text' ||
        part.type === 'formula' ||
        part.type === 'blockFormula'
    )
  );

  // Step 3: Insert content
  filteredLines.forEach((line) => {
    const mergedText = line
      .filter((part) => part.type === 'text')
      .map((part) => part.value)
      .join(' ');

    let hasInsertedBlockFormula = false;

    if (mergedText.trim()) {
      const html = converter.makeHtml(mergedText);
      const delta = editor.clipboard.convert(html);
      editor.updateContents([{ retain: index }, ...delta.ops]);
      index += delta.length();
    }

    // Insert formulas
    line.forEach((part) => {
      if (part.type === 'formula') {
        editor.insertEmbed(index, 'formula', part.value);
        index += 1;
      } else if (part.type === 'blockFormula') {
        editor.insertText(index, '\n');
        index += 1;

        editor.insertEmbed(index, 'formula', part.value);
        index += 1;

        editor.insertText(index, '\n');
        index += 1;

        hasInsertedBlockFormula = true;
      }
    });

    if (!hasInsertedBlockFormula) {
      editor.insertText(index, '\n');
      index += 1;
    }
  });

  editor.setSelection(index);
};

export const parseMarkdownWithFormulas = ({ markdown }) => {
  const parts = [];
  let index = 0;
  const length = markdown.length;

  const flushText = (text) => {
    if (text) {
      parts.push({ type: 'markdown', value: text.replace(/\\\$/g, '$') });
    }
  };

  while (index < length) {
    if (markdown.startsWith('$$', index)) {
      const end = markdown.indexOf('$$', index + 2);
      if (end !== -1) {
        flushText(markdown.slice(0, index));
        const content = markdown.slice(index + 2, end);
        parts.push({ type: 'blockFormula', value: content.trim() });
        markdown = markdown.slice(end + 2);
        index = 0;
        continue;
      }
    }

    if (markdown[index] === '$' && markdown[index - 1] !== '\\') {
      const end = markdown.indexOf('$', index + 1);
      if (end !== -1 && markdown[end - 1] !== '\\') {
        flushText(markdown.slice(0, index));
        const content = markdown.slice(index + 1, end);
        parts.push({ type: 'formula', value: content.trim() });
        markdown = markdown.slice(end + 1);
        index = 0;
        continue;
      }
    }

    index++;
  }

  flushText(markdown);

  return parts;
};
